import { STEPS } from 'constants/Steps';

import actions from 'actions';
import { PROPERTY_TYPES, STEP_ID_NAME, STEP_NUMBER_NAME } from 'constants/Global';

/* eslint-disable no-param-reassign */

/**
 * Map _property_type field for ReduxForm.
 * @param {Object} estima Estima received from API
 */
export function apiToFormPropertyType(estima) {
    if (estima.item_type) {
        const matchingTypes = PROPERTY_TYPES.filter(
            (e) =>
                e.type === estima.item_type.key &&
                (!estima.item_subtype ||
                    (estima.item_subtype && e.subType === estima.item_subtype.key)),
        );

        if (matchingTypes.length > 0) {
            estima._property_type = matchingTypes[0].id;
        }
    }
}

/**
 * Map location related fields for ReduxForm.
 * @param {Object} estima Estima received from API
 */
export function apiToFormLocalisation(estima) {
    if (estima.address) {
        estima.address_name = estima.address.name;
    }

    if (estima.city) {
        estima.zip = estima.city.zip;
        estima.city_name = estima.city.name;
        estima._city_id = estima.city.id;
    }
}

/**
 * Map count fields for ReduxForm.
 * @param {Object} estima Estima received from API
 */
export function apiToFormCounts(estima) {
    estima.room_count = estima.rooms || 1;
    delete estima.rooms;

    estima.bathroom_count = estima.bathrooms || 1;
    delete estima.bathrooms;

    estima.floor_count = estima.floors || 1;
    delete estima.floors;

    estima.secondary_room_count = estima.secondary_rooms || 1;
    delete estima.secondary_rooms;

    estima.level_count = estima.level_count || 0;
}

/**
 * Map profile related for ReduxForm.
 * @param {Object} estima Estima received from API
 */
export function apiToFormProfile(estima) {
    if (estima.profile) {
        estima.profile_owner = estima.profile.owner;
        estima.profile_sale_method = estima.profile.sale_method;
        estima.profile_optin_realtors = estima.profile.realtors_optin;
        estima.profile_buyer = estima.profile.buyer;
        estima.profile_incentive = estima.profile.buy_incentive;
        estima.profile_needs_loan_status = estima.profile.loan_status;
        estima.profile_needs_to_sell = estima.profile.buyer_sell_profile;
        estima.profile_needs_loan = estima.profile.loan;

        if (estima.profile.residence_type) {
            estima.profile_residence_type = estima.profile.residence_type.key;
        }

        if (estima.profile.seller_profile) {
            estima.profile_seller = estima.profile.seller_profile.key;
        }
    }
}

/**
 * Map form enum keys related for ReduxForm.
 * @param {Object} estima Estima received from API
 */
export function apiToFormEnumKey(estima) {
    if (estima.ground_garden) {
        estima.ground_garden = estima.ground_garden.key;
    }

    if (estima.relative_perception) {
        estima.relative_perception = estima.relative_perception.key;
    }

    if (estima.lightness) {
        estima.lightness = estima.lightness.key;
    }

    if (estima.peacefulness) {
        estima.peacefulness = estima.peacefulness.key;
    }

    if (estima.roof_quality) {
        estima.roof_quality = estima.roof_quality.key;
    }

    if (estima.standing) {
        estima.standing = estima.standing.key;
    }

    if (estima.transport_proximity) {
        estima.transport_proximity = estima.transport_proximity.key;
    }

    if (estima.ownership && estima.ownership.type) {
        estima.ownership_type = estima.ownership.type.key;
    }

    if (estima.renovation_level) {
        estima.renovation_level = estima.renovation_level.key;
    }
}

/**
 * Delete unused keys in ReduxForm.
 * @param {Object} estima Estima received from API
 */
export function apiToFormClean(estima) {
    Object.keys(estima)
        .filter((e) => typeof estima[e] === 'object' && e !== 'coordinates')
        .forEach((e) => delete estima[e]);
}

/**
 * Remove value from form for asking user to update them.
 * @param {Object} estima Estima received from API
 */
export function cleanBarometre(estima) {
    delete estima.profile_seller;
    delete estima.profile_sale_method;
    delete estima.profile_optin_realtors;
    delete estima.profile_buyer;
    delete estima.profile_incentive;
    delete estima.profile_needs_loan_status;
    delete estima.profile_needs_to_sell;
    delete estima.profile_needs_loan;
}

/* eslint-enable no-param-reassign */

/**
 * Map API object to ReduxForm shape.
 * @param {Object} state Previous ReduxForm state
 * @param {Object} estima API Object
 * @return {Object} ReduxForm compatible shape
 */
export function apiToForm(state, estima) {
    const formEstima = { ...estima, previous_estima_id: estima.id };

    apiToFormPropertyType(formEstima);
    apiToFormLocalisation(formEstima);
    apiToFormCounts(formEstima);
    apiToFormProfile(formEstima);
    apiToFormEnumKey(formEstima);
    apiToFormClean(formEstima);

    cleanBarometre(formEstima);

    return {
        ...state,
        values: {
            ...state.values,
            ...formEstima,
        },
    };
}

/**
 * Determine if phone is required from given values.
 * @param  {Object}  values Form values
 * @return {Boolean} Phone is required
 */
export function isPhoneRequired(values) {
    if (values.profile_optin_realtors) {
        return typeof values.user_phone !== 'string' || !/\S/.test(values.user_phone);
    }

    return true;
}

/**
 * Common form cases
 * @param {Object} state
 * @param {Object} action
 * @return {Object}
 */
export function commonFormPlugin(state, action) {
    switch (action.type) {
        case actions.VALID_STEP:
            if (action.isValid) {
                return {
                    ...state,
                    asyncErrors: null,
                };
            }
            return state;
        case actions.CHANGE_STEP:
            return {
                ...state,
                values: {
                    ...state.values,
                    [STEP_NUMBER_NAME]: action.step,
                    [STEP_ID_NAME]: STEPS[action.step - 1] ? STEPS[action.step - 1].id : null,
                },
            };
        default:
            return state;
    }
}
